import { useActivityMenus } from '@model/activity';
import { message } from '@tencent/tea-component';
import Menu from '@tencent/tpm-meeting-active/lib/Menu';
import '@tencent/tpm-meeting-active/lib/Menu/style';
import { useRouter } from 'next/router';
import React, { FC, useMemo, VFC, useCallback, useEffect } from 'react';
import { useGoLink } from 'src/hooks/useGoLink';
import { useActivityBriefInfo } from 'src/hooks/useActivityInfo';
import confirm from 'src/components/saleModal/method';
import DashboardEventUpdateVenueType from '@quickService/DashboardEventUpdateVenueType';
import SvgBasic from './icon/basic.svg';
import SvgMarketing from './icon/marketing.svg';
import SvgPeople from './icon/people.svg';
import SvgSchedule from './icon/schedule.svg';
import SvgTicket from './icon/ticket.svg';
import SvgVenus from './icon/venues.svg';
import SvgPlayback from './icon/playback.svg';
import SvgSurvey from './icon/survey-icon.svg';
import SvgDataCenter from './icon/data-center.svg';
import SvgBasicActive from './icon/basic-active.svg';
import SvgMarketingActive from './icon/marketing-active.svg';
import SvgPeopleActive from './icon/people-active.svg';
import SvgScheduleActive from './icon/schedule-active.svg';
import SvgTicketActive from './icon/ticket-active.svg';
import SvgVenusActive from './icon/venues-active.svg';
import SvgPlaybackActive from './icon/playback-active.svg';
import SvgSurveyActive from './icon/survey-icon-active.svg';
import SvgDataCenterActive from './icon/data-center-active.svg';
import useProgressChange from 'src/hooks/useProccessChange';
import dataReport from '@utils/data-report';
import { useTranslation } from 'react-i18next';
import { useSWRConfig } from 'swr';

import { PayBadge } from '@tencent/calendar-web-component-pc-biz';
import useCommercialize from '@hooks/useCommercialize';
/** Link包裹: 由于 Link 是不创建实际 element 的, 导致MenuItem里会一层样式的问题 */
const TempLink: FC<any> = ({ children, href, ...props }) => {
  const goLink = useGoLink();
  return (
    <div {...props} onClick={() => goLink(href)}>
      {children}
    </div>
  );
};

interface IMenu {
  id: string;
  name: string;
  icon?: string | string[];
  path?: string;
  children?: IMenu[];
  report?: string;
  hasPay?: boolean; // 是否需要付费
  venueType?: number;
  dividingLine?: boolean;
}

export const MENUS: IMenu[] = [
  {
    id: 'overview',
    name: '活动总览',
    path: '/event/[eventid]',
    icon: [SvgSurvey, SvgSurveyActive],
    report: 'e#event_navigation#overview_sec#click',
  },
  {
    id: 'basic_info',
    name: '基础信息',
    icon: [SvgBasic, SvgBasicActive],
    path: '/event/[eventid]/basic',
    report: 'e#event_navigation#basic_sec#click',
  },
  {
    id: 'venue',
    name: '活动场地',
    icon: [SvgVenus, SvgVenusActive],
    path: '/event/[eventid]/venue',
    report: 'e#event_navigation#venue_sec#click',
    children: [
      { id: 'stage', name: '大型会场', path: '/event/[eventid]/venue/stage', venueType: 1 },
      { id: 'session', name: '小型讨论室', path: '/event/[eventid]/venue/session', venueType: 2 },
      { id: 'lobby_config', name: '品牌设置', path: '/event/[eventid]/venue/logo' },
    ],
  },
  {
    id: 'people',
    name: '参会人员',
    icon: [SvgPeople, SvgPeopleActive],
    path: '/event/[eventid]/members',
    children: [
      {
        id: 'guest',
        name: '活动嘉宾',
        path: '/event/[eventid]/members/guest',
        report: 'e#event_navigation#panalist_sec#click',
      },
      {
        id: 'audience',
        name: '观众管理',
        path: '/event/[eventid]/members/audience',
        report: 'e#event_navigation#registrant_sec#click',
      },
    ],
  },
  {
    id: 'schedule',
    name: '活动日程',
    icon: [SvgSchedule, SvgScheduleActive],
    path: '/event/[eventid]/schedule',
    report: 'e#event_navigation#schedule_sec#click',
  },
  {
    id: 'register',
    name: '报名管理',
    icon: [SvgTicket, SvgTicketActive],
    path: '/event/[eventid]/registration',
    children: [
      {
        id: 'registration-access',
        name: '准入管理',
        path: '/event/[eventid]/registration/access',
        report: 'e#event_navigation#registrationpage_setting_access#click',
      },
      {
        id: 'ticket',
        name: '门票管理',
        path: '/event/[eventid]/registration/tickets',
        report: 'e#event_navigation#ticket_sec#click',
      },
      {
        id: 'registration-page',
        name: '报名页装修',
        path: '/event/[eventid]/registration/register',
        report: 'e#event_navigation#registrationpage_setting_sec#click',
      },
      {
        id: 'question',
        name: '报名问题',
        path: '/event/[eventid]/registration/question',
        report: 'e#event_navigation#regist_sec#click',
        hasPay: true,
      },
      {
        id: 'approval',
        name: '报名审批',
        path: '/event/[eventid]/registration/approval',
        report: 'e#event_navigation#registrant_review_sec#click',
      },
    ],
  },
  {
    id: 'data-center',
    name: '数据中心',
    icon: [SvgDataCenter, SvgDataCenterActive],
    path: '/event/[eventid]/data-center',
    report: 'e#event_navigation#data_center#click',
    dividingLine: true,
  },
  {
    id: 'marketing',
    name: '渠道统计',
    icon: [SvgMarketing, SvgMarketingActive],
    path: '/event/[eventid]/marketing',
    hasPay: true,
  },
  {
    id: 'playback',
    name: '活动回放',
    icon: [SvgPlayback, SvgPlaybackActive],
    path: '/event/[eventid]/playback',
    report: 'e#event_navigation#replay_sec#click',
  },
];

const calcPath = (path: string, query: Record<string, any>) =>
  path.replace(/(\[(\w+)\])/g, (_a, _b, k) => (query[k] as string) || 'unknown');

const NavMenu: VFC<{
  data: IMenu[];
  commercialize?: any;
  activityInfo?: any;
  mutate?: any;
}> = ({ data, commercialize = {}, activityInfo, mutate }) => {
  if (!data || data.length <= 0) return <></>;
  const router = useRouter();
  const { pathname, query } = router;
  const { validateStatus } = useProgressChange();
  const { t } = useTranslation();
  const { mutate: venueMutate } = useSWRConfig();
  const parentPathName = pathname.split('/').slice(0, -1).join('/');
  // 根据是否勾选场地类型来控制是否出现弹窗
  const isShowModal = useCallback(
    (menu) => {
      console.log('menu', menu, activityInfo?.venue_type);
      if (menu?.venueType && !activityInfo?.venue_type?.includes(menu?.venueType as number)) {
        const name = menu?.venueType === 1 ? t('stage.name') : t('session.name');
        confirm({
          title: `是否开启${name}？`,
          content: `您未开启${name}，是否确认开启？`,
        }).then(
          () => {
            // 点击确定，留在当前页，顺便刷新一下场地和装修列表
            const editedVenueType = activityInfo?.venue_type?.concat(menu?.venueType as number);
            const listSwr = {
              url: '/v2/activities/dashboard/venue/query-by-type-v2',
              params: {
                event_id: query.eventid as string,
                venue_type: menu?.venueType,
              },
            };
            DashboardEventUpdateVenueType({
              event_id: query.eventid as string,
              venue_type: editedVenueType as number[],
            }).then(
              (res) => {
                // 开启某种场地类型后，需要重新验证发布修改的状态
                validateStatus();
                message.success({
                  content: res.msg || '已成功开启',
                });
                mutate();
                // 刷新一下场地和装修列表
                venueMutate(listSwr);
              },
              (err) => {
                message.error({
                  content: err.msg || '开启失败，请重试',
                });
                mutate();
                // 刷新一下场地和装修列表
                venueMutate(listSwr);
              },
            );
          },
          () => {
            // 点击取消，回到上一页
            history.back();
          },
        );
      }
    },
    [activityInfo, router],
  );
  useEffect(() => {
    if (!activityInfo?.venue_type) return;
    const parentPathName = pathname.split('/').slice(0, -1).join('/');
    if (parentPathName === '/event/[eventid]/venue') {
      const menuParent = MENUS.find((item) => item.id === 'venue');
      const menu = menuParent?.children?.find((item) => item?.path === pathname);
      isShowModal(menu);
    }
  }, [pathname, activityInfo?.venue_type]);

  // 获取上一级菜单的路径

  const renderMenu = (menu: IMenu) => {
    if (menu.children?.length) {
      return (
        <Menu.SubMenu
          title={menu.name}
          icon={menu.icon}
          key={menu.id}
          opened={parentPathName === menu.path}
        >
          <NavMenu
            data={menu.children}
            commercialize={commercialize}
            activityInfo={activityInfo}
            mutate={mutate}
          />
        </Menu.SubMenu>
      );
    }
    return renderMenuItem(menu, commercialize);
  };

  const renderMenuItem = (menu: IMenu, commercialize?: any) => {
    let title: React.ReactNode = menu.name;
    if (menu.id === 'stage') {
      title = t('stage.name');
    }
    if (menu.id === 'session') {
      title = t('session.name');
    }
    if (menu.hasPay && commercialize?.[menu.id] === '0') {
      title = (
        <>
          {title}
          <PayBadge className="menuPayBadge" type="mid"></PayBadge>
        </>
      );
    }
    return (
      <>
        {!!menu.dividingLine && <div className="sidebar-dividingLine" />}
        <Menu.Item
          element={TempLink}
          href={calcPath(menu.path!, query)}
          title={title}
          icon={menu.icon}
          key={menu.id}
          data-path={menu.path}
          selected={pathname === menu.path}
          onClick={() => {
            dataReport(menu.report);
          }}
        />
      </>
    );
  };

  return <>{data.map((menu: IMenu) => renderMenu(menu))}</>;
};

const EventMenus: FC = () => {
  const router = useRouter();
  const { query } = router;
  const { data: menuList } = useActivityMenus({ event_id: query.eventid as string });
  const { activityInfo, mutate } = useActivityBriefInfo({ event_id: query.eventid as string });

  const { vipResource } = useCommercialize();
  const commercialize = useMemo(
    () => ({
      question: vipResource?.rule?.event_question_type_bit,
      marketing: vipResource?.rule?.channel_statistics,
    }),
    [vipResource?.rule],
  );
  const filteredMenu: IMenu[] = useMemo(() => {
    // 未请求到菜单数据时隐藏
    if (!menuList) {
      return [];
    }
    if (!Object.keys(menuList).length) {
      return MENUS;
    }
    // 过滤出来在白名单里的、且有path或children的菜单项
    // prettier-ignore
    const childrenFilter = (children?: IMenu[]): IMenu[] => (children || [])
      .filter(c => (menuList[c.id] && (c.path || c.children?.length)))
      .map(child => ({
        ...child,
        children: childrenFilter(child.children),
      }));
    // TODO: 需要后台配置新的菜单
    // return MENUS;
    return childrenFilter(childrenFilter(MENUS));
  }, [menuList]);
  /** 指引弹框出现时机
   *  前一个路由为总览 下一个路由为非总览
   * 当前活动guide为0
   * */
  return (
    <Menu>
      <NavMenu
        data={filteredMenu}
        commercialize={commercialize}
        activityInfo={activityInfo}
        mutate={mutate}
      />
    </Menu>
  );
};

export default EventMenus;
